import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Newspaper, Clock, Calendar, ExternalLink, MessageCircle, Users, PenLine, Link2, ImageIcon, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const NewsCard = ({ item, showSummary = true }) => (
  <Link
    to={item.slug ? `/news/${item.slug}` : "#"}
    className="block bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md hover:border-[#2BAEE5] transition-all"
    data-testid={`news-${item.id}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <h3 className="font-semibold text-[#0F172A] mb-2 group-hover:text-[#7C2C3B] leading-tight">
          {item.headline}
        </h3>
        {showSummary && item.summary && (
          <p className="text-sm text-slate-600 mb-3 line-clamp-5">{item.summary}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="font-medium text-[#7C2C3B]">{item.source}</span>
          {item.published_at && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(item.published_at).toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          )}
        </div>
      </div>
      <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
    </div>
  </Link>
);

const MatchReviewCard = ({ review }) => (
  <Link
    to={review.slug ? `/news/${review.slug}` : "#"}
    className="block bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-md hover:border-[#2BAEE5] transition-all"
    data-testid={`review-${review.id}`}
  >
    <div className="bg-gradient-to-r from-[#7C2C3B] to-[#63232F] p-3">
      <div className="flex items-center justify-between text-white">
        <span className="text-sm font-medium">{review.competition}</span>
        <span className="text-lg font-bold">{review.score}</span>
      </div>
      <div className="text-white/80 text-xs mt-1">
        {new Date(review.match_date).toLocaleDateString('en-GB', { 
          weekday: 'short',
          day: 'numeric', 
          month: 'short',
          year: 'numeric'
        })}
      </div>
    </div>
    <div className="p-4">
      <h3 className="font-semibold text-[#0F172A] mb-2 text-sm">{review.headline}</h3>
      <p className="text-sm text-slate-600 mb-3 leading-relaxed">{review.summary}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[#7C2C3B]">{review.source}</span>
        <span className="flex items-center gap-1 text-xs text-[#2BAEE5]">
          Read full review
          <ExternalLink className="w-3 h-3" />
        </span>
      </div>
    </div>
  </Link>
);

const SocialCard = ({ item }) => (
  <a
    href={item.source_url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 p-3 hover:shadow-md hover:border-[#2BAEE5] transition-all"
    data-testid={`social-${item.id}`}
  >
    <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0">
      <span className="text-white font-bold text-lg">𝕏</span>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-[#0F172A] line-clamp-2">{item.headline}</p>
      <span className="text-xs text-[#2BAEE5] font-medium">{item.source}</span>
    </div>
    <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0" />
  </a>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="mb-8">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-[#7C2C3B]" />
      <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
        {title.toUpperCase()}
      </h2>
    </div>
    {children}
  </div>
);

const BlogCard = ({ blog }) => (
  <Link
    to={blog.slug ? `/news/${blog.slug}` : "#"}
    className="block bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-md hover:border-[#2BAEE5] transition-all"
    data-testid={`blog-${blog.id}`}
  >
    {blog.images && blog.images.length > 0 && blog.images[0] && (
      <img src={blog.images[0]} alt="" className="w-full h-48 object-cover" />
    )}
    <div className="p-5">
      <h3 className="font-bold text-lg text-[#0F172A] mb-2 leading-tight">{blog.title}</h3>
      <p className="text-sm text-slate-600 mb-4 leading-relaxed line-clamp-5 whitespace-pre-line">
        {blog.content}
      </p>
      <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
        <span className="font-medium text-[#7C2C3B]">{blog.author || "ALLWHUFC"}</span>
        {blog.published_at && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(blog.published_at).toLocaleDateString('en-GB', {
              day: 'numeric', month: 'short', year: 'numeric'
            })}
          </span>
        )}
      </div>
    </div>
  </Link>
);

export default function NewsPage() {
  const [mainTab, setMainTab] = useState("feed");
  const [selectedTeam, setSelectedTeam] = useState("men");
  const [latestNews, setLatestNews] = useState([]);
  const [allNews, setAllNews] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [social, setSocial] = useState([]);
  const [archive, setArchive] = useState({ articles: [], total: 0, page: 1, pages: 0 });
  const [showArchive, setShowArchive] = useState(false);
  const [archivePage, setArchivePage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [allNewsPage, setAllNewsPage] = useState(1);
  const ALL_NEWS_PER_PAGE = 10;
  const [blogs, setBlogs] = useState([]);
  const [blogPage, setBlogPage] = useState(1);
  const [blogTotal, setBlogTotal] = useState(0);
  const [blogPages, setBlogPages] = useState(0);
  const [loadingBlogs, setLoadingBlogs] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    fetchAllData();
  }, [selectedTeam]);

  // Auto-retry when news is empty (uses state counter to guarantee re-renders)
  useEffect(() => {
    if (!loading && latestNews.length === 0 && allNews.length === 0 && retryCount < 8) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        fetchAllData();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [loading, latestNews, allNews, retryCount]);

  useEffect(() => {
    if (showArchive) {
      fetchArchive();
    }
  }, [showArchive, archivePage, selectedTeam]);

  useEffect(() => {
    if (mainTab === "blog") {
      fetchBlogs();
    }
  }, [mainTab, blogPage]);

  const fetchBlogs = async () => {
    setLoadingBlogs(true);
    try {
      const res = await axios.get(`${API}/blogs?page=${blogPage}&limit=10`);
      setBlogs(res.data.blogs || []);
      setBlogTotal(res.data.total || 0);
      setBlogPages(res.data.pages || 0);
    } catch (error) {
      console.error("Failed to fetch blogs:", error);
    } finally {
      setLoadingBlogs(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setAllNewsPage(1);
    try {
      const [latestRes, allRes, reviewsRes, socialRes] = await Promise.all([
        axios.get(`${API}/news/latest?team=${selectedTeam}`),
        axios.get(`${API}/news/all?team=${selectedTeam}`),
        axios.get(`${API}/news/reviews?team=${selectedTeam}`),
        axios.get(`${API}/news/social?team=${selectedTeam}`)
      ]);
      setLatestNews(latestRes.data);
      setAllNews(allRes.data);
      setReviews(reviewsRes.data);
      setSocial(socialRes.data);
    } catch (error) {
      console.error("Failed to fetch news:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchive = async () => {
    setLoadingArchive(true);
    try {
      const res = await axios.get(`${API}/news/archive?team=${selectedTeam}&page=${archivePage}`);
      setArchive(res.data);
    } catch (error) {
      console.error("Failed to fetch archive:", error);
    } finally {
      setLoadingArchive(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="news-page">
      <SEO title="News" path="/news" description="Latest West Ham United news, transfer rumours, match reports and analysis. Stay up to date with all the Hammers headlines." />
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#7C2C3B] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-2">
            <Newspaper className="w-10 h-10 text-[#F8D742]" />
            <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
              NEWS & REVIEWS
            </h1>
          </div>
          <p className="text-white/80">Latest headlines, match reviews, blog posts and social updates</p>
        </div>
      </div>

      {/* Main Tab Selector: News Feed vs ALLWHUFC News Blog */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
            <TabsList className="grid w-full max-w-lg grid-cols-2">
              <TabsTrigger value="feed" data-testid="tab-news-feed" className="flex items-center gap-2">
                <Newspaper className="w-4 h-4" />
                News Feed
              </TabsTrigger>
              <TabsTrigger value="blog" data-testid="tab-allwhufc-news" className="flex items-center gap-2">
                <PenLine className="w-4 h-4" />
                ALLWHUFC News
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* ALLWHUFC News Blog Tab */}
      {mainTab === "blog" && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="blog-section">
          <div className="flex items-center gap-2 mb-6">
            <PenLine className="w-6 h-6 text-[#7C2C3B]" />
            <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
              ALLWHUFC NEWS
            </h2>
          </div>
          {loadingBlogs ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <PenLine className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No blog posts yet</p>
              <p className="text-slate-400 text-sm mt-1">Check back soon for ALLWHUFC original content</p>
            </div>
          ) : (
            <>
              <div className="grid gap-6">
                {blogs.map(blog => (
                  <BlogCard key={blog.id} blog={blog} />
                ))}
              </div>
              {blogPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8" data-testid="blog-pagination">
                  <button
                    onClick={() => setBlogPage(p => Math.max(1, p - 1))}
                    disabled={blogPage <= 1}
                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium disabled:opacity-40 hover:bg-slate-200 transition-colors"
                  >
                    Previous
                  </button>
                  {Array.from({ length: blogPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => setBlogPage(p)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                        blogPage === p ? "bg-[#7C2C3B] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setBlogPage(p => Math.min(blogPages, p + 1))}
                    disabled={blogPage >= blogPages}
                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium disabled:opacity-40 hover:bg-slate-200 transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* News Feed Tab */}
      {mainTab === "feed" && (
      <>
      {/* Team Selector */}
      <div className="bg-white border-b border-slate-200 sticky top-[112px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Tabs value={selectedTeam} onValueChange={setSelectedTeam} className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="men" data-testid="tab-men" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Men's Team
              </TabsTrigger>
              <TabsTrigger value="women" data-testid="tab-women" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Women's Team
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : latestNews.length === 0 && allNews.length === 0 && retryCount < 8 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Loader2 className="w-8 h-8 text-[#7C2C3B] animate-spin mb-4" />
            <p className="text-slate-600 font-medium">Loading latest news...</p>
            <p className="text-slate-400 text-sm mt-1">Content is being compiled. This page will update automatically.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Column - News */}
            <div className="lg:col-span-2 space-y-8">
              {/* Latest News */}
              <Section title="Latest News" icon={Clock}>
                <p className="text-sm text-slate-500 mb-4">News from the last 24 hours</p>
                {latestNews.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No latest news available</p>
                ) : (
                  <div className="space-y-3">
                    {latestNews.map(item => (
                      <NewsCard key={item.id} item={item} />
                    ))}
                  </div>
                )}
              </Section>

              {/* All News */}
              <Section title="All News" icon={Newspaper}>
                <p className="text-sm text-slate-500 mb-4">News from the past month</p>
                {allNews.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No news available</p>
                ) : (
                  <>
                    <div className="space-y-3">
                      {allNews.slice((allNewsPage - 1) * ALL_NEWS_PER_PAGE, allNewsPage * ALL_NEWS_PER_PAGE).map(item => (
                        <NewsCard key={item.id} item={item} />
                      ))}
                    </div>
                    {allNews.length > ALL_NEWS_PER_PAGE && (
                      <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-slate-100" data-testid="all-news-pagination">
                        <button
                          onClick={() => setAllNewsPage(p => Math.max(1, p - 1))}
                          disabled={allNewsPage <= 1}
                          className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium disabled:opacity-40 hover:bg-slate-200 transition-colors"
                          data-testid="all-news-prev"
                        >
                          Previous
                        </button>
                        {Array.from({ length: Math.ceil(allNews.length / ALL_NEWS_PER_PAGE) }, (_, i) => i + 1).map(pageNum => (
                          <button
                            key={pageNum}
                            onClick={() => setAllNewsPage(pageNum)}
                            className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                              allNewsPage === pageNum
                                ? "bg-[#7C2C3B] text-white"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                            data-testid={`all-news-page-${pageNum}`}
                          >
                            {pageNum}
                          </button>
                        ))}
                        <button
                          onClick={() => setAllNewsPage(p => Math.min(Math.ceil(allNews.length / ALL_NEWS_PER_PAGE), p + 1))}
                          disabled={allNewsPage >= Math.ceil(allNews.length / ALL_NEWS_PER_PAGE)}
                          className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium disabled:opacity-40 hover:bg-slate-200 transition-colors"
                          data-testid="all-news-next"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </Section>

              {/* Match Reviews */}
              <Section title="Match Reviews" icon={Calendar}>
                <p className="text-sm text-slate-500 mb-4">In-depth match analysis and reports</p>
                {reviews.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No match reviews available</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {reviews.map(review => (
                      <MatchReviewCard key={review.id} review={review} />
                    ))}
                  </div>
                )}
              </Section>
            </div>

            {/* Sidebar - Social */}
            <div>
              <Section title="Social Headlines" icon={MessageCircle}>
                <p className="text-sm text-slate-500 mb-4">From journalists & fan accounts</p>
                {social.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No social updates available</p>
                ) : (
                  <div className="space-y-3">
                    {social.map(item => (
                      <SocialCard key={item.id} item={item} />
                    ))}
                  </div>
                )}
              </Section>

              {/* Key Accounts to Follow */}
              <div className="bg-white rounded-lg border border-slate-200 p-4 mt-6">
                <h3 className="font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                  KEY ACCOUNTS TO FOLLOW
                </h3>
                <div className="space-y-2 text-sm">
                  <a href="https://twitter.com/ExWHUemployee" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-600 hover:text-[#2BAEE5]">
                    <span className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold">𝕏</span>
                    @ExWHUemployee
                  </a>
                  <a href="https://twitter.com/WestHamWayCom" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-600 hover:text-[#2BAEE5]">
                    <span className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold">𝕏</span>
                    @WestHamWayCom
                  </a>
                  <a href="https://twitter.com/JacobSteinberg" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-600 hover:text-[#2BAEE5]">
                    <span className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold">𝕏</span>
                    @JacobSteinberg
                  </a>
                  <a href="https://twitter.com/claretandhugh" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-600 hover:text-[#2BAEE5]">
                    <span className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold">𝕏</span>
                    @claretandhugh
                  </a>
                  <a href="https://twitter.com/westhamwomen" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-600 hover:text-[#2BAEE5]">
                    <span className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold">𝕏</span>
                    @westhamwomen
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* News Archive Section */}
        <div className="mt-8 border-t border-slate-200 pt-8">
          <button
            onClick={() => setShowArchive(!showArchive)}
            className="flex items-center gap-2 text-[#7C2C3B] hover:text-[#63232F] font-semibold mb-4"
          >
            <Newspaper className="w-5 h-5" />
            <span style={{ fontFamily: 'Barlow Condensed' }}>
              {showArchive ? "HIDE NEWS ARCHIVE" : "VIEW NEWS ARCHIVE"} (Older Articles)
            </span>
          </button>
          
          {showArchive && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                  ARCHIVED NEWS ({archive.total} articles)
                </h3>
                {archive.pages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setArchivePage(p => Math.max(1, p - 1))}
                      disabled={archivePage <= 1}
                      className="px-3 py-1 rounded bg-slate-100 text-slate-600 disabled:opacity-50 text-sm"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-slate-500">
                      Page {archivePage} of {archive.pages}
                    </span>
                    <button
                      onClick={() => setArchivePage(p => Math.min(archive.pages, p + 1))}
                      disabled={archivePage >= archive.pages}
                      className="px-3 py-1 rounded bg-slate-100 text-slate-600 disabled:opacity-50 text-sm"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
              
              {loadingArchive ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#7C2C3B] border-t-transparent"></div>
                </div>
              ) : archive.articles.length > 0 ? (
                <div className="grid gap-3">
                  {archive.articles.map(item => (
                    <NewsCard key={item.id} item={item} showSummary={false} />
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No archived articles found</p>
              )}
            </div>
          )}
        </div>
      </div>
      </>
      )}

      <Footer />
    </div>
  );
}
